import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  copyQuestions, errorDetail, fetchLibrary, fetchOverviewDetail, fetchSubgroups, humanError,
} from '../api';
import { ConfirmPanel } from '../components/ConfirmPanel';
import { Icon } from '../components/icons';
import { WorkspaceShell } from '../components/WorkspaceShell';
import { withAdminOnly } from '../components/withAdminOnly';
import { useDebounced } from '../hooks';
import { BLOCK_LABEL, skippedSummary } from '../model';
import type { LibraryItem, OverviewDetail, TestBlock } from '../model';
import { pushNotice, takeNotice } from '../notices';
import type { WorkspaceNotice } from '../notices';
import {
  clearPickedRows, clearSelection, loadPickedRows, loadSelection, mergeIds, savePickedRows, saveSelection, toggleId,
} from '../selectionStore';
import type { PickedRow } from '../selectionStore';
import { addSourceItems, loadDraft, MAX_ITEMS, saveDraft } from '../wizardStore';
import { questionsLabel, questionTypeLabel, testsIn } from '../words';

/** Строк в одной выдаче и предел `POST …/questions/copy` за один раз. */
const LIMIT = 200;
const MAX_PICK = 200;

const BLOCK_CHIPS: Array<['' | TestBlock, string]> = [
  ['', 'Все разделы'],
  ['products', BLOCK_LABEL.products],
  ['vehicles', BLOCK_LABEL.vehicles],
];

interface Lock {
  label: string;
  already: boolean;
}

interface Props {
  /** `test` — добавить в существующий тест; `wizard` — собрать вопросы для нового теста. */
  target: 'test' | 'wizard';
}

const QuestionPickerPage: React.FC<Props> = ({ target }) => {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const params = useParams();
  const testId = target === 'test' ? Number(params.testId) : 0; // ключ выбора: у мастера 0

  const [detail, setDetail] = useState<OverviewDetail | null>(null);
  const [draft] = useState(() => (target === 'wizard' ? loadDraft() : null));
  const [query, setQuery] = useState('');
  const debouncedSearch = useDebounced(query.trim(), 250);
  const search = query.trim() ? debouncedSearch : ''; // очистка поля действует сразу, без лишнего запроса со старым словом
  const [block, setBlock] = useState<'' | TestBlock>('');
  const [subgroup, setSubgroup] = useState('');
  const [subgroups, setSubgroups] = useState<string[]>([]);
  const [rows, setRows] = useState<LibraryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [ids, setIds] = useState<number[]>(() => loadSelection(testId));
  const [known] = useState(
    () => new Map<number, PickedRow>(loadPickedRows(testId).map((row): [number, PickedRow] => [row.question_id, row])),
  );
  const [notice, setNotice] = useState<WorkspaceNotice | null>(null);
  const [confirmAttempts, setConfirmAttempts] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const requestRef = useRef(0);

  useEffect(() => {
    // Не в инициализаторе useState: под <React.StrictMode> компонент рендерится дважды, второй вызов вернул бы null.
    const incoming = takeNotice();
    if (incoming) setNotice(incoming);
  }, []);

  useEffect(() => {
    if (target !== 'test') return undefined;
    if (!Number.isInteger(testId) || testId < 1) {
      pushNotice({ kind: 'info', text: 'Такого теста нет.' }); // /admin/tests/abc/questions/add
      navigateRef.current('/admin/tests', { replace: true });
      return undefined;
    }
    let cancelled = false;
    fetchOverviewDetail(testId)
      .then((data) => {
        if (cancelled) return;
        if (data.status === 'archived') {
          pushNotice({ kind: 'info', text: 'Тест в архиве. Сначала верните его из архива.' });
          navigateRef.current(`/admin/tests/${testId}`, { replace: true });
          return;
        }
        setDetail(data);
      })
      .catch((error) => {
        if (cancelled) return;
        if ((error as { response?: { status?: number } } | null)?.response?.status === 404) {
          pushNotice({ kind: 'info', text: 'Такого теста нет.' });
          navigateRef.current('/admin/tests', { replace: true });
        }
      });
    return () => { cancelled = true; };
  }, [target, testId]);

  useEffect(() => {
    let cancelled = false;
    fetchSubgroups()
      .then((list) => { if (!cancelled) setSubgroups(list); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  const load = useCallback(async () => {
    const request = ++requestRef.current; // устаревший ответ не должен затереть свежий
    setLoading(true);
    setFailed(false);
    try {
      const data = await fetchLibrary({
        forTestId: target === 'test' ? testId : undefined,
        search,
        block: block || undefined,
        subgroup: subgroup || undefined,
        limit: LIMIT,
      });
      if (request !== requestRef.current) return;
      data.items.forEach((row) => known.set(row.question_id, { question_id: row.question_id, text: row.text, type: row.type }));
      setRows(data.items);
      setTotal(data.total);
    } catch {
      if (request === requestRef.current) setFailed(true);
    } finally {
      if (request === requestRef.current) setLoading(false);
    }
  }, [target, testId, search, block, subgroup, known]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadRef = useRef(load);
  loadRef.current = load; // действия после добавления перезагружают список с ТЕКУЩИМИ фильтрами, а не с теми, что были при нажатии

  useEffect(() => {
    saveSelection(testId, ids);
    savePickedRows(testId, ids.flatMap((id) => { const row = known.get(id); return row ? [row] : []; }));
  }, [ids, testId, known]);

  const draftIds = new Set((draft?.items ?? []).flatMap((item) => (item.kind === 'source' ? [item.id] : [])));
  const draftSize = draft?.items.length ?? 0;
  const maxPick = target === 'wizard' ? Math.min(MAX_PICK, Math.max(0, MAX_ITEMS - draftSize)) : MAX_PICK;
  const capReason = maxPick < MAX_PICK
    ? `в новом тесте может быть не больше ${MAX_ITEMS} вопросов`
    : `за один раз можно добавить не больше ${MAX_PICK} вопросов`;

  const lockOf = (row: LibraryItem): Lock | null => {
    if (row.already_in_test || draftIds.has(row.question_id)) return { label: 'Уже в тесте', already: true };
    if (row.has_conflict) return { label: 'Есть расхождение в правильных ответах', already: false };
    if (!row.structure_ok) {
      return {
        label: row.problems.length > 0 ? `В вопросе ошибка: ${row.problems.join('; ')}` : 'В вопросе ошибка',
        already: false,
      };
    }
    return null;
  };
  const locks = new Map(rows.map((row): [number, Lock | null] => [row.question_id, lockOf(row)]));
  const selectable = rows.filter((row) => locks.get(row.question_id) === null);
  const lockedIds = new Set(rows.filter((row) => locks.get(row.question_id) !== null).map((row) => row.question_id));
  const fresh = ids.filter((id) => !lockedIds.has(id));
  const freshSet = new Set(fresh);
  const toMark = selectable.filter((row) => !freshSet.has(row.question_id));
  const room = Math.max(0, maxPick - fresh.length);
  const allMarked = selectable.length > 0 && toMark.length === 0;
  const partial = toMark.length > room;

  let selectAllLabel = 'Отметить все найденные';
  if (allMarked) selectAllLabel = 'Все найденные отмечены';
  else if (partial && room > 0) selectAllLabel = `Отметить первые ${room}`;
  else if (selectable.length > 0) selectAllLabel = `Отметить все найденные (${selectable.length})`;
  const selectAllDisabled = selectable.length === 0 || allMarked || room === 0;

  const hints: string[] = [];
  if (fresh.length > maxPick) {
    hints.push(`Отмечено ${questionsLabel(fresh.length)}, а ${capReason}. Снимите лишние отметки.`);
  } else if (rows.length > 0 && selectable.length === 0) {
    hints.push(
      rows.every((row) => locks.get(row.question_id)?.already)
        ? 'Все найденные вопросы уже есть в тесте.'
        : 'Среди найденных нет вопросов, которые можно добавить: у каждого есть пометка.',
    );
  } else if (partial && room > 0) {
    hints.push(`Отметятся первые ${room} из ${toMark.length}: ${capReason}.`);
  } else if (partial) {
    hints.push(`Отмечено ${questionsLabel(fresh.length)}: ${capReason}. Добавьте их, потом выберите остальные.`);
  }
  if (total > rows.length) hints.push(`Показаны первые ${rows.length} из ${total}. Уточните поиск, чтобы увидеть остальные.`);

  const toggle = (id: number) => {
    if (!freshSet.has(id) && fresh.length >= maxPick) {
      setNotice({ kind: 'info', text: `Больше отметить нельзя: ${capReason}.` });
      return;
    }
    setIds((current) => toggleId(current, id));
  };
  const selectAllFound = () => setIds((current) => mergeIds(current, toMark.slice(0, room).map((row) => row.question_id)));

  const cancel = () => navigate(target === 'wizard' ? '/admin/tests/new?step=2' : `/admin/tests/${testId}`);
  const showAll = () => {
    setQuery('');
    setBlock('');
    setSubgroup('');
  };

  const addSelected = async (acknowledge: boolean) => {
    if (busyRef.current || fresh.length === 0) return;
    busyRef.current = true;
    setBusy(true);
    try {
      if (target === 'wizard') {
        const picked = fresh.flatMap((id) => { const row = known.get(id); return row ? [row] : []; });
        const current = loadDraft();
        const next = addSourceItems(current, picked);
        const added = next.items.length - current.items.length;
        clearSelection(0);
        clearPickedRows(0);
        if (added === 0) {
          setIds([]);
          setNotice({ kind: 'info', text: 'Эти вопросы уже есть в тесте.' });
          return;
        }
        saveDraft(next);
        pushNotice({ kind: 'ok', text: `Добавлено: ${questionsLabel(added)}. Они внизу, в списке «Вопросы в этом тесте».` });
        navigate('/admin/tests/new?step=2');
        return;
      }
      const result = await copyQuestions(testId, fresh, acknowledge);
      const skipped = skippedSummary(result.skipped);
      if (result.copied.length > 0) {
        clearSelection(testId);
        clearPickedRows(testId);
        pushNotice({
          kind: 'ok',
          text: [`Добавлено: ${questionsLabel(result.copied.length)}. Они в конце списка.`, skipped].filter(Boolean).join(' '),
        });
        navigate(`/admin/tests/${testId}`);
        return;
      }
      setIds([]);
      setNotice({ kind: 'info', text: ['Ничего не добавлено.', skipped].filter(Boolean).join(' ') });
      void loadRef.current();
    } catch (error) {
      const info = errorDetail(error);
      if (info?.code === 'test_has_completed_attempts') {
        setConfirmAttempts(Number(info.completed_attempts) || 0);
      } else if (info?.code === 'questions_not_found') {
        const missing = Array.isArray(info.missing_ids) ? (info.missing_ids as number[]) : [];
        setIds((current) => current.filter((id) => !missing.includes(id)));
        setNotice({
          kind: 'info',
          text: 'Некоторых вопросов больше нет в системе. Мы убрали их из выбора. Проверьте список и добавьте ещё раз.',
        });
        void loadRef.current();
      } else if (info?.code === 'test_is_archived') {
        pushNotice({ kind: 'info', text: 'Тест в архиве. Сначала верните его из архива.' });
        navigate(`/admin/tests/${testId}`, { replace: true });
      } else {
        setNotice({ kind: 'info', text: humanError(error, 'Не получилось добавить вопросы. Попробуйте ещё раз.') });
      }
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const testTitle = target === 'wizard' ? draft?.title.trim() : detail?.title;
  let where = target === 'wizard' ? 'в новый тест' : 'в этот тест';
  if (testTitle) where = `в тест «${testTitle}»`;
  const filtersOn = search !== '' || block !== '' || subgroup !== '';
  const addDisabled = fresh.length === 0 || busy || confirmAttempts !== null || fresh.length > maxPick;

  const basket = (
    <div className="tw-basket">
      <div className="tw-basket-in">
        {confirmAttempts !== null && (
          <div style={{ flex: '1 1 100%' }}>
            <ConfirmPanel
              title="Тест уже проходили"
              text={`Тест уже проходили: ${confirmAttempts}. Проценты у прошедших при ближайшем пересчёте будут посчитаны от нового числа вопросов.`}
              confirmLabel="Да, добавить"
              cancelLabel="Нет, не добавлять"
              busy={busy}
              onConfirm={() => { setConfirmAttempts(null); void addSelected(true); }}
              onCancel={() => setConfirmAttempts(null)}
            />
          </div>
        )}
        <span className="tw-basket-count">
          {fresh.length > 0 ? `Выбрано: ${questionsLabel(fresh.length)}` : 'Пока ничего не выбрано'}
        </span>
        <span className="tw-basket-actions">
          <button type="button" className="tw-btn" onClick={cancel}>Отмена</button>
          <button type="button" className="tw-btn tw-btn-primary" disabled={addDisabled} onClick={() => void addSelected(false)}>
            <Icon name="plus" />
            Добавить выбранные в тест
          </button>
        </span>
        {fresh.length === 0 && <span className="tw-help tw-basket-hint">Отметьте галочкой хотя бы один вопрос.</span>}
      </div>
    </div>
  );

  return (
    <WorkspaceShell notice={notice} onCloseNotice={() => setNotice(null)} basket={basket}>
      <section className="tw-screen">
        <div className="tw-row">
          <button type="button" className="tw-btn tw-btn-quiet" onClick={cancel}>
            <Icon name="left" />
            {target === 'wizard' ? 'Вернуться к созданию теста' : 'Вернуться к тесту'}
          </button>
        </div>
        <div>
          <h1>Готовые вопросы</h1>
          <p className="tw-lead" style={{ marginTop: 8 }}>
            {`Здесь все вопросы, которые уже есть в системе. Отметьте галочкой нужные — они добавятся ${where}.`}
          </p>
        </div>
        <div className="tw-warn">
          <Icon name="info" />
          <span>В тест попадёт копия вопроса. Старые тесты при этом не изменятся.</span>
        </div>

        <div className="tw-card tw-stack-sm">
          <div>
            <label className="tw-label" htmlFor="tw-pq">Найти вопрос</label>
            <div className="tw-search">
              <Icon name="search" />
              <input
                id="tw-pq"
                className="tw-field"
                type="text"
                maxLength={200}
                placeholder="Например: тормозной диск"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
            </div>
          </div>
          <div>
            <div className="tw-label">Раздел вопросов</div>
            <div className="tw-row" role="group" aria-label="Раздел вопросов">
              {BLOCK_CHIPS.map(([key, label]) => (
                <button key={label} type="button" className="tw-chip" aria-pressed={block === key} onClick={() => setBlock(key)}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="tw-label" htmlFor="tw-psub">Тема вопросов</label>
            <select id="tw-psub" className="tw-field" value={subgroup} onChange={(event) => setSubgroup(event.target.value)}>
              <option value="">Все темы</option>
              {subgroups.map((name) => <option key={name} value={name}>{name}</option>)}
            </select>
          </div>
          {total > 0 && <p className="tw-help">{`Найдено вопросов: ${total}`}</p>}
          <div className="tw-row tw-btn-row">
            <button type="button" className="tw-btn" disabled={selectAllDisabled} onClick={selectAllFound}>
              <Icon name="check" />
              {selectAllLabel}
            </button>
            <button type="button" className="tw-btn" disabled={fresh.length === 0} onClick={() => setIds([])}>
              Снять все отметки
            </button>
          </div>
          {hints.map((hint) => <p key={hint} className="tw-help">{hint}</p>)}
        </div>

        {loading && rows.length === 0 && !failed && (
          <div className="tw-card tw-empty" aria-busy="true">
            <p className="tw-help">Загружаем вопросы…</p>
          </div>
        )}
        {failed && (
          <div className="tw-card tw-empty">
            <Icon name="info" size={44} large />
            <h2>Не получилось загрузить вопросы</h2>
            <p className="tw-help">Проверьте соединение и попробуйте ещё раз.</p>
            <button type="button" className="tw-btn tw-btn-primary" onClick={() => void load()}>Повторить</button>
          </div>
        )}

        <ul className="tw-qlist">
          {rows.map((row) => {
            const lock = locks.get(row.question_id) ?? null;
            const checked = !lock && freshSet.has(row.question_id);
            return (
              <li key={row.question_id}>
                <label className={`tw-pick${checked ? ' tw-pick-on' : ''}${lock ? ' tw-pick-off' : ''}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={Boolean(lock)}
                    onChange={() => toggle(row.question_id)}
                  />
                  <span className="tw-pick-body">
                    <span className="tw-pick-title">{row.text}</span>
                    <span className="tw-pick-text">{`${questionTypeLabel(row.type)} · Тема: ${row.subgroup ?? 'без темы'}`}</span>
                    <span className="tw-pick-text">
                      {row.tests_count > 0 ? `Сейчас есть в ${testsIn(row.tests_count)}` : 'Пока нет ни в одном тесте'}
                    </span>
                    {lock && <span className={`tw-pill ${lock.already ? 'tw-pill-given' : 'tw-pill-fail'}`}>{lock.label}</span>}
                    {row.has_conflict && !lock?.already && (
                      <a className="tw-pick-text" href="/admin/questions" target="_blank" rel="noreferrer">
                        Разобраться можно в «Банке вопросов»
                      </a>
                    )}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>

        {!loading && !failed && rows.length === 0 && filtersOn && (
          <div className="tw-card tw-empty">
            <Icon name="search" size={44} large />
            <h2>Таких вопросов нет</h2>
            <p className="tw-help">Проверьте, как написано слово, или покажите все вопросы.</p>
            <button type="button" className="tw-btn tw-btn-primary" onClick={showAll}>Показать все вопросы</button>
          </div>
        )}
        {!loading && !failed && rows.length === 0 && !filtersOn && (
          <div className="tw-card tw-empty">
            <Icon name="file" size={44} large />
            <h2>Пока нет ни одного готового вопроса</h2>
            <p className="tw-help">Вернитесь назад и напишите первый вопрос сами.</p>
          </div>
        )}
      </section>
    </WorkspaceShell>
  );
};

export default withAdminOnly(QuestionPickerPage);
